"use client"

import React, { useState, useEffect, useRef } from "react"
import { Link as ScrollLink } from "react-scroll"

import { TableOfContentsProps } from "@/interface"

const TableOfContents: React.FC<TableOfContentsProps> = ({ headings }) => {
  const [activeId, setActiveId] = useState("")
  const headerOffset = 70
  const tocRef = useRef<HTMLUListElement>(null)
  const activeItemRef = useRef<HTMLLIElement>(null)

  const handleClick = (id: string) => {
    setActiveId(id)
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  }

  // Auto-scroll TOC to keep active heading visible within the scrollable container
  useEffect(() => {
    if (activeItemRef.current && tocRef.current) {
      const tocContainer = tocRef.current.closest(".overflow-auto")
      if (tocContainer) {
        const activeElement = activeItemRef.current
        const containerRect = tocContainer.getBoundingClientRect()
        const activeRect = activeElement.getBoundingClientRect()

        const isAboveView = activeRect.top < containerRect.top
        const isBelowView = activeRect.bottom > containerRect.bottom

        if (isAboveView || isBelowView) {
          const activeOffsetTop = activeElement.offsetTop - tocRef.current.offsetTop

          let scrollTop
          if (isAboveView) {
            scrollTop = activeOffsetTop - 20
          } else {
            scrollTop = activeOffsetTop - containerRect.height + activeRect.height + 20
          }

          tocContainer.scrollTo({
            top: scrollTop,
            behavior: "smooth",
          })
        }
      }
    }
  }, [activeId])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((entry) => entry.isIntersecting)
        if (visibleEntries.length > 0) {
          const firstVisibleId = visibleEntries[0]?.target?.id
          if (firstVisibleId) setActiveId(firstVisibleId)
        }
      },
      { rootMargin: `-${headerOffset}px 0px -80% 0px`, threshold: [0.6, 1] }
    )

    document.querySelectorAll("h2, h3").forEach((element) => observer.observe(element))

    return () => observer.disconnect()
  }, [])

  return (
    <aside className="flex flex-col gap-6 rounded-lg border border-[#0000001F] p-6 md:border-none md:p-0">
      <div className="font-medium tracking-[-1.6%] text-[#3F3F3FBF] md:hidden md:text-xl">On this page</div>
      <ul ref={tocRef} className="flex flex-col gap-4 pr-2">
        {headings.map((heading, index) => {
          if (heading.depth === 3) {
            const childH3s: { id: string; text: string; depth: number }[] = []
            for (let i = index + 1; i < headings.length; i++) {
              const h3 = headings[i]
              if (!h3 || h3.depth !== 4) break
              childH3s.push(h3)
            }

            const isH2Active = activeId === heading.id
            const isAnyH3Active = childH3s.some((h3) => activeId === h3.id)
            const isCurrentSectionActive = isH2Active || isAnyH3Active

            return (
              <li key={heading.id} ref={isCurrentSectionActive ? activeItemRef : null}>
                <ScrollLink
                  to={heading.id}
                  smooth={true}
                  duration={500}
                  offset={-headerOffset}
                  onClick={() => handleClick(heading.id)}
                  className={`cursor-pointer text-sm font-medium leading-[150%] tracking-[-1.6%] transition-colors duration-300 ${
                    isH2Active ? "text-[#1E1E1E]" : "text-[#3F3F3FBF]"
                  }`}
                >
                  {heading.text}
                </ScrollLink>

                {childH3s.length > 0 && (
                  <ul
                    className={`ml-3 flex flex-col gap-2 overflow-hidden transition-[max-height] duration-500 ease-in-out ${
                      isH2Active || isAnyH3Active ? "mt-2 max-h-96 opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    {childH3s.map((h3) => {
                      const isH3Active = activeId === h3.id
                      return (
                        <li key={h3.id} ref={isH3Active ? activeItemRef : null}>
                          <ScrollLink
                            to={h3.id}
                            smooth={true}
                            duration={500}
                            offset={-headerOffset}
                            onClick={() => handleClick(h3.id)}
                            className={`cursor-pointer text-sm font-medium tracking-[-1.6%] transition-colors duration-300 ${
                              isH3Active ? "text-[#1E1E1E]" : "text-[#3F3F3FBF]"
                            }`}
                          >
                            {h3.text}
                          </ScrollLink>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </li>
            )
          }
          return null
        })}
      </ul>
    </aside>
  )
}

export default TableOfContents
